<template>
  <div class="order-details-dialog">
    <el-dialog
        v-loading="loading"
        :fullscreen="true"
        :close-on-press-escape="false"
        :show-close="false"
        :close-on-click-modal="false"
        :visible.sync="visible">
      <div
          id="order-details-title"
          class="order-details-title-bar">
        <div class="order-number">
          Return #{{ returnOrderDetails.id }}
        </div>
        <button
            type="button"
            class="el-dialog__headerbtn"
            aria-label="Close"
            @click="$emit('update:visible', false)">
          <i class="el-dialog__close el-icon el-icon-close"></i>
        </button>
      </div>
      <div
          v-if="!loading"
          id="order-details-body"
          class="order-details-body">
        <div class="order-details-timeline">
          <ReturnOrderTimeline
              :return-order="returnOrder" />
        </div>
        <div style="border: 1px solid #DEE5EC;"></div>
        <div
            style="  flex: 3;overflow-y: scroll;display: flex;flex-direction: column;">
          <div
              v-show="generateLabelFailed"
              class="error-message"
              style="background: var(--light-negative-muted, #fcefef); color: var(--light-negative-base, #d4231a);">
            Return label was not generated. Please check the customer address or try again later.
            <button
                type="button"
                class="el-dialog__headerbtn"
                aria-label="Close"
                @click="generateLabelFailed=false">
              <i
                  class="el-dialog__close el-icon el-icon-close"
                  style="color: #d4231a;"></i>
            </button>
          </div>
          <div
              v-show="generateLabelSuccessful"
              class="error-message"
              style="background: var(--light-positive-muted, #F0F8F0); color: var(--light-positive-base, #178019);">
            Return label has been generated and sent to {{ returnOrderDetails.customer.address_1 }}. See the Return Label
            <button
                type="button"
                class="el-dialog__headerbtn"
                aria-label="Close"
                @click="generateLabelSuccessful=false">
              <i
                  class="el-dialog__close el-icon el-icon-close"
                  style="color:#178019;"></i>
            </button>
          </div>
          <div
              id="order-details-data"
              class="order-details-data">
            <div class="flex flex-row items-center">
              <div class="order-number">
                Return #{{ returnOrderDetails.id }}
              </div>
              <OrderStatus :order-status="returnOrderDetails.status" />
            </div>
            <div
                class="flex flex-row items-center"
                style="margin: 16px 0">
              <div style="font-weight: 500">
                Date Placed:
              </div>
              <div style="margin-left: 4px">
                <time-zone-formatter
                    output-format="MMM DD, YYYY"
                    :current-time="returnOrderDetails.created_at" />
              </div>
              <div style="font-weight: 500; margin-left: 26px">
                Email:
              </div>
              <div style="margin-left: 4px">
                {{ returnOrderDetails.customer.address_1 }}
              </div>
            </div>
            <div id="order-details-items">
              <return-order-details-item-view
                  v-for="(item, idx) in returnOrderDetails.return_items"
                  :key="idx"
                  v-bind="item"
                  style="margin-bottom: 8px" />
            </div>
            <div
                class="order-details-item"
                style="font-weight: 500; margin-left: 0px; margin-top: 36px;">
              Shipping
            </div>
            <div
                v-if="returnOrderDetails.return_labels.length === 0"
                class="item-details">
              <shipment-details-available-soon />
            </div>
            <div
                v-else
                id="order-details-item">
              <return-order-shipping-view
                  v-for="(item,idx) in returnOrderDetails.return_labels"
                  :key="idx"
                  :tracking-id="item.tracking_id"
                  style="margin-bottom: 8px" />
            </div>
            <div
                class="order-details-item"
                style="font-weight: 500; margin-left: 0px; margin-top: 36px;">
              Payment
            </div>
            <div id="order-details-item">
              <el-card
                  class-name="item-card"
                  shadow="never"
                  style="border-radius: 4px !important; border: 1px solid #dee5ec; margin-right: 12px;"
                  body-style="margin: 8px;">
                <div
                    class="flex flex-row"
                    style="margin: 8px 8px 8px 8px">
                  <div
                      class="flex-1"
                      style="margin: 7px">
                    <div class="item-id flex flex-row">
                      <div class="flex-1">
                        Additional fees
                      </div>
                      <div>{{ formattedAmount( additional_fees.amount, additional_fees.currency_code) }}</div>
                    </div>
                    <el-divider />
                    <div
                        class="item-name flex flex-row">
                      <div class="flex-1 flex">
                        <div>
                          Total refund
                        </div>
                      </div>
                      <div>
                        {{ formattedAmount(returnOrderDetails.refund_info.amount, returnOrderDetails.refund_info.currency) }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>
            <div
                class="order-details-item flex flex-row"
                style="font-weight: 500">
              <div class="flex-1 flex">
                <div style="font-weight: 500; margin-left: 0px; margin-top: 24px;">
                  Customer Info
                </div>
              </div>
            </div>
            <div
                id="order-details-items">
              <el-card
                  class="item-card"
                  shadow="never"
                  style="border-radius: 4px !important; border: 1px solid #dee5ec;"
                  body-style="margin: 0px;">
                <div
                    class="flex flex-row"
                    style="margin: 8px 24px 8px 8px">
                  <div
                      class="flex-1"
                      style="margin: 8px">
                    <div
                        class="flex flex-row"
                        style="margin: 0px 0px 8px 0px">
                      <div class="flex-1">
                        <strong>Delivery Address</strong>
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Name
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.name }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Phone number
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.phone }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div
                          class="flex-1"
                          style="margin: 0px 0px 24px 0px">
                        Email
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.address_1 }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Street
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.address_1 }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        City
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.city }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        State/province/area
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.state }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Zip code
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.zip }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Country
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.country }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>
          </div>
        </div>
      </div>
      <div
          id="order-details-footer"
          class="order-details-footer">
        <el-dropdown
            trigger="click"
            @command="handleAction">
          <el-button
              plain
              class="choose_action_button">
            Choose Action <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              style="max-height: unset !important; overflow: unset !important;">
            <el-dropdown-item
                class="choose_action_item"
                command="resend_label">
              Resend Label
            </el-dropdown-item>
            <el-dropdown-item
                :disabled="!returnOrderDetails.closable"
                class="choose_action_item action_cancel"
                command="cancel_return">
              Cancel Return
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </el-dialog>
    <ConfirmCancel
        v-if="showCancelConfirmation"
        :visible.sync="showCancelConfirmation"
        @cancel-return="confirmCancel" />
  </div>
</template>

<script>
import axios from "axios";
import OrderStatus from "../OrderStatus.vue";
import ReturnOrderDetailsItemView from "./ReturnOrderDetailsItemView.vue";
import ShipmentDetailsAvailableSoon from "../ShipmentDetailsAvailableSoon";
import ReturnOrderShippingView from "./ReturnOrderShippingView.vue";
import numeral from "numeral";
import ConfirmCancel from "./ConfirmCancel.vue";
import ReturnOrderTimeline from "./ReturnOrderTimeline.vue";
import "../../../../assets/order_details.css";
import { createNamespacedHelpers as h } from "vuex";
const { mapActions: requestActions, mapGetters: requestGetters } =
  h("db/requests");

export default {
  name: "ReturnOrderDetails",
  components: {
    ConfirmCancel,
    ReturnOrderShippingView,
    ShipmentDetailsAvailableSoon,
    ReturnOrderDetailsItemView,
    OrderStatus,
    ReturnOrderTimeline,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      required: true,
    },
    returnId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: true,
      returnOrderDetails: {},
      showCancelConfirmation: false,
      currencySymbols: {
        EUR: "€",
        NONE: "",
        USD: "$",
        GBP: "£",
        AUD: "A$",
        NZD: "N$",
        CAD: "C$",
      },
      additional_fees: {
        currency_code: "",
        amount: 0,
      },
      returnOrder: {},
      generateLabelSuccessful: false,
      generateLabelFailed: false,
    };
  },
  mounted() {
    this.getReturnOrderDetails();
  },
  methods: {
    getReturnOrderDetails() {
      this.loading = true;
      axios
        .get(`v3/returns/return_order_details`, {
          params: {
            order_id: this.order.order.displayable_order_id,
            id: this.returnId,
          },
        })
        .then((response) => {
          if (response) {
            this.returnOrder = response.data;
            this.returnOrderDetails = response.data.return_order;
            if (this.returnOrderDetails.refund_info) {
              if (
                this.returnOrderDetails.refund_info.total_additional_fees_set
                  .shop_money
              ) {
                this.additional_fees =
                  this.returnOrderDetails.refund_info.total_additional_fees_set.shop_money;
              }
            } else {
              this.returnOrderDetails.refund_info = {};
            }
            this.returnOrderDetails.status =
              this.returnOrderDetails.status.replaceAll("_", " ");
            let words = this.returnOrderDetails.status.split(" ");
            this.returnOrderDetails.status = "";
            for (let word of words) {
              word = word.charAt(0).toUpperCase() + word.slice(1);
              this.returnOrderDetails.status =
                this.returnOrderDetails.status + " " + word;
            }
            this.returnOrderDetails.status =
              this.returnOrderDetails.status.trim();

            let returnItem = [];
            for (
              let i = 0;
              i < this.returnOrderDetails.return_items.length;
              i++
            ) {
              if (this.returnOrderDetails.return_items[i].bundle_items) {
                returnItem = returnItem.concat(
                  this.getNonBundleItems(
                    this.returnOrderDetails.return_items[i]
                  )
                );
              } else {
                let item = [];
                item = this.getReturnProductDetails(
                  this.returnOrderDetails.return_items[i]
                );
                for (
                  let j = 0;
                  j < this.returnOrderDetails.return_items[i].quantity;
                  j++
                ) {
                  returnItem = returnItem.concat(item);
                }
              }
            }
            this.returnOrderDetails.return_items = returnItem;
          }
        })
        .catch((error) => {
          console.log(error);
          this.$message({
            type: "error",
            message: `Can't fetch return order details data. Try again.`,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    ...requestActions(["generateReturnLabel"]),
    handleAction(command) {
      if (command === "resend_label") {
        this.getReturnOrderLabel();
      } else if (command === "cancel_return") {
        this.showCancelConfirmation = true;
      }
    },
    getReturnOrderLabel() {
      this.loading = true;
      axios
        .post(`v3/returns/resend_label`, {
          order_id: this.order.order.displayable_order_id,
          return_order_id: this.returnId,
          source: "ND",
        })
        .then((response) => {
          let return_labels = response.data.return_order.return_labels;
          this.tracking_id =
            return_labels[return_labels.length - 1].tracking_id;
          this.generateLabelSuccessful = true;
          setTimeout(() => {
            this.generateLabelSuccessful = false;
          }, 10000);
        })
        .catch((error) => {
          this.generateLabelFailed = true;
          setTimeout(() => {
            this.generateLabelFailed = false;
          }, 10000);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formattedAmount(amount, currency) {
      let neg = false;
      if (amount < 0) {
        neg = true;
        amount = -amount;
      }
      amount = numeral(amount).format(`0,0.00`);
      return `${neg ? "– " : ""}${
        this.currencySymbols[currency || "USD"]
      }${amount}`;
    },
    getNonBundleItems(return_item) {
      let return_items = [];
      let item = [];
      let items = return_item.bundle_items;
      for (let i = 0; i < items.length; i++) {
        item = this.getReturnProductDetails(items[i]);
        for (let j = 0; j < items[i].quantity; j++) {
          return_items = return_items.concat(item);
        }
      }
      return return_items;
    },
    confirmCancel() {
      this.loading = true;
      axios
        .post(`v3/returns/close_return`, {
          order_id: this.order.order.displayable_order_id,
          return_order_id: this.returnId,
          source: "ND",
        })
        .then((response) => {
          this.$message({
            type: "success",
            message: `Return cancelled successfully!`,
          });
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: `Return could not be cancelled.`,
          });
        })
        .finally(() => {
          this.loading = false;
          this.showCancelConfirmation = false;
          this.getReturnOrderDetails();
        });
    },
    getReturnProductDetails(return_item) {
      let item = {
        title: "",
        sku: "",
        images: "",
        status: "",
      };
      for (let i = 0; i < this.returnOrderDetails.products.length; i++) {
        if (this.returnOrderDetails.products[i].sku === return_item.sku) {
          item.title = this.returnOrderDetails.products[i].title;
          item.sku = return_item.sku;
          item.images = this.returnOrderDetails.products[i].images;
          item.id = this.returnOrderDetails.products[i].id;
          item.status = this.returnOrderDetails.status;
        }
      }
      return item;
    },
  },
};
</script>

<style>
.error-message {
  display: flex;
  width: 1068px;
  padding: 16px 40px;
  align-items: flex-start;
  gap: 16px;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Equip;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
}
</style>
